```vue
<template>
  <div>
    <!-- Cabeçalho -->
    <div class="d-flex justify-content-between align-items-center mb-3">
      <h5 class="mb-0">Funcionários</h5>
      <b-button
        v-if="hasPermission('adicionar_funcionarios')"
        variant="primary"
        class="d-flex align-items-center"
        @click="irParaCadastro"
      >
        <i class="bi bi-plus-circle-fill" title="Novo Cadastro"></i>
      </b-button>
    </div>

    <!-- Filtros -->
    <div class="d-flex mb-3" style="gap: 10px;">
      <input
        v-model="filters.nome"
        placeholder="Filtrar por nome"
        class="form-control"
        @input="filtrar"
      />
      <select
        v-model="filters.cargo_id"
        class="form-select"
        style="max-width:200px"
        @change="filtrar"
      >
        <option :value="undefined">Filtrar por cargo</option>
        <option v-for="c in cargos" :key="c.id" :value="c.id">{{ c.nome }}</option>
      </select>
      <select
        v-model="filters.ativo"
        class="form-select"
        style="max-width:200px"
        @change="filtrar"
      >
        <option :value="undefined">Filtrar por ativo</option>
        <option :value="true">Ativo</option>
        <option :value="false">Inativo</option>
      </select>
      <b-button variant="secondary" @click="limparFiltros" class="d-flex align-items-center">
        <i class="bi bi-funnel" title="Limpar Filtros"></i>
      </b-button>
    </div>

    <!-- Tabela customizada -->
    <table class="table table-striped table-hover">
      <thead>
        <tr>
          <th>
            <button class="btn btn-link p-0" @click="sort('pessoa_fisica_nome')">
              Nome {{ arrow('pessoa_fisica_nome') }}
            </button>
          </th>
          <th>
            <button class="btn btn-link p-0" @click="sort('cargo_nome')">
              Cargo {{ arrow('cargo_nome') }}
            </button>
          </th>
          <th>
            <button class="btn btn-link p-0" @click="sort('ativo')">
              Ativo {{ arrow('ativo') }}
            </button>
          </th>
          <th class="text-end">Ações</th>
        </tr>
      </thead>
      <tbody>
        <tr v-for="f in funcionarios" :key="f.id">
          <td>{{ f.pessoa_fisica_nome }}</td>
          <td>{{ f.cargo_nome }}</td>
          <td>{{ f.ativo ? 'Ativo' : 'Inativo' }}</td>
          <td class="text-end">
            <i
              v-if="hasPermission('detalhar_funcionarios')"
              class="bi bi-eye-fill text-primary mx-2"
              title="Detalhar"
              @click="detalharFuncionario(f.id)"
            ></i>
            <i
              v-if="hasPermission('alterar_funcionarios')"
              class="bi bi-pencil-fill text-warning mx-2"
              title="Editar"
              @click="editarFuncionario(f.id)"
            ></i>
            <i
              v-if="hasPermission('excluir_funcionarios')"
              class="bi bi-trash-fill text-danger mx-2"
              title="Excluir"
              @click="excluirFuncionario(f.id)"
            ></i>
          </td>
        </tr>
      </tbody>
    </table>

    <!-- Paginação -->
    <div class="d-flex justify-content-between align-items-center mt-3">
      <!-- 1. Resumo -->
      <div>
        Mostrando {{ startRow }} a {{ endRow }} de {{ totalItems }} registros
      </div>

      <!-- 2. Seletor de itens por página -->
      <div class="d-flex align-items-center" style="gap: .5rem;">
        <b-form-select
          v-model="limitePorPagina"
          :options="pageOptions.map(n => ({ value: n, text: n + ' / página' }))"
          size="sm"
          style="width: 6rem"
          @change="alterarLimite"
        />
      </div>

      <!-- 3. Controle de página -->
      <div class="d-flex align-items-center">
        <b-pagination
          v-model="paginaAtual"
          :per-page="limitePorPagina"
          :total-rows="totalItems"
          size="sm"
          limit="5"
          @input="load"
        />
      </div>
    </div>
  </div>
</template>

<script setup>
import { ref, computed, onMounted, getCurrentInstance, watch } from 'vue';
import { useRouter } from 'vue-router';
import axios from '@/services/axios';
import { BButton, BPagination, BFormSelect } from 'bootstrap-vue-next';
import { useSortableTable } from '@/utils/useSortableTable';
import { usePermissions } from '@/composables/usePermissions';

const router = useRouter();
const { proxy } = getCurrentInstance();
const { fetchPermissions, hasPermission } = usePermissions();

// carrega cargos para filtro
const cargos = ref([]);

async function carregarCargos() {
  try {
    const { data } = await axios.get('/cadastros/cargos-funcionarios', {
      params: { page: 1, limit: 10000 }
    });

    const lista = data?.data ?? data ?? [];

    const somenteAtivos = Array.isArray(lista)
      ? lista.filter(c => c.ativo !== false) // mantém se ativo=true ou undefined
      : [];

    somenteAtivos.sort((a, b) =>
      String(a.nome ?? a.cargo ?? '')
        .localeCompare(String(b.nome ?? b.cargo ?? ''), 'pt-BR', {
          sensitivity: 'base',
          numeric: true
        })
    );

    cargos.value = somenteAtivos;
  } catch (error) {
    console.error('Erro ao carregar cargos:', error);
    cargos.value = [];
  }
}

// composable de listagem/pagination
const {
  items: funcionarios,
  totalItems,          // total de registros
  page: paginaAtual,
  limit: limitePorPagina,
  filters,
  load,
  sort,
  arrow,
} = useSortableTable(
  `/cadastros/funcionarios`,
  { key: 'pessoa_fisica_nome', order: 'asc' }
);

// opções de limite de página
const pageOptions = [10, 15, 20, 25, 50];

// computeds de resumo
const startRow = computed(() =>
  (paginaAtual.value - 1) * limitePorPagina.value + 1
);
const endRow = computed(() =>
  Math.min(paginaAtual.value * limitePorPagina.value, totalItems.value)
);

watch(paginaAtual, (novo, antigo) => {
  if (novo !== antigo) {
    load();
  }
});

// Quando o limite mudar, volta para a página 1 e recarrega
watch(limitePorPagina, (novo, antigo) => {
  if (novo !== antigo) {
    paginaAtual.value = 1;
    load();
  }
});


onMounted(async () => {
  await fetchPermissions();
  await carregarCargos();
  filters.value = { nome: '', cargo_id: undefined, ativo: undefined };
  load();
});

// handlers de filtro
function filtrar() {
  paginaAtual.value = 1;
  load();
}
function limparFiltros() {
  filters.value = { nome: '', cargo_id: undefined, ativo: undefined };
  paginaAtual.value = 1;
  load();
}
function alterarLimite() {
  paginaAtual.value = 1;
  load();
}

// navegação e CRUD
function irParaCadastro() {
  router.push('/cadastros/funcionarios/novo');
}
function detalharFuncionario(id) {
  router.push(`/cadastros/funcionarios/${id}/detalhar`);
}
function editarFuncionario(id) {
  router.push(`/cadastros/funcionarios/${id}`);
}
async function excluirFuncionario(id) {
  const ok = await proxy.$confirm(
    'Tem certeza que deseja excluir este funcionário?',
    'Confirmação'
  );
  if (!ok) return;
  const resp = await axios.delete(`/cadastros/funcionarios/${id}`);
  if (resp.data.blocked) {
    await proxy.$alert(resp.data.message);
    return;
  }
  load();
  proxy.$toast.success('Funcionário excluído');
}
</script>

<style scoped>

:deep(.pagination.pagination-sm .page-link) {
  padding: rem rem;
  line-height: 1;
  font-size: 0.8rem !important;
  border-radius: 0 !important;
}

</style>